
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import * as sessionApi from '@/api/sessionApi';
import * as processingUtils from '@/utils/processingUtils';
import type { Stem } from '@/types/stem';
import type { SessionType } from '@/api/sessionApi';

export const useStemSession = () => {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingDuration, setProcessingDuration] = useState(0);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [activeSession, setActiveSession] = useState<{ id: string | null, type: SessionType }>({ id: null, type: 'stem' });

  const { data: sessions, isLoading: isLoadingStemSessions } = useQuery({
    queryKey: ['stemSessions', 'stem'],
    queryFn: () => sessionApi.fetchSessions('stem'),
  });

  const { data: acapellaSessions, isLoading: isLoadingAcapellaSessions } = useQuery({
    queryKey: ['stemSessions', 'acapella'],
    queryFn: () => sessionApi.fetchSessions('acapella'),
  });

  const { data: drumSessions, isLoading: isLoadingDrumSessions } = useQuery({
    queryKey: ['stemSessions', 'drum'],
    queryFn: () => sessionApi.fetchSessions('drum'),
  });

  const isLoadingSessions = isLoadingStemSessions || isLoadingAcapellaSessions || isLoadingDrumSessions;

  const createSession = useMutation({
    mutationFn: sessionApi.createSession,
    onSuccess: (newSession) => {
      queryClient.invalidateQueries({ queryKey: ['stemSessions', newSession.type] });
      setActiveSession({ id: newSession.id, type: newSession.type as SessionType });
    },
    onError: (error) => {
      console.error('Error in createSession mutation:', error);
      toast.error('Failed to create session');
    }
  });

  const updateSession = useMutation({
    mutationFn: sessionApi.updateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stemSessions'] });
    },
    onError: (error) => {
      console.error('Error in updateSession mutation:', error);
      toast.error('Failed to update session');
    }
  });

  const deleteSession = useMutation({
    mutationFn: ({ id }: { id: string }) => sessionApi.deleteSession(id),
    onSuccess: (_, { id }) => {
      if (activeSession.id === id) {
        setActiveSession({ id: null, type: activeSession.type });
      }
      queryClient.invalidateQueries({ queryKey: ['stemSessions'] }).then(() => {
        const remainingSessions = activeSession.type === 'stem'
          ? sessions?.filter(s => s.id !== id)
          : activeSession.type === 'acapella'
          ? acapellaSessions?.filter(s => s.id !== id)
          : drumSessions?.filter(s => s.id !== id);
        if (remainingSessions?.length > 0 && activeSession.id === id) {
          setActiveSession({ id: remainingSessions[0].id, type: activeSession.type });
        }
      });
      toast.success('Session deleted successfully');
    },
    onError: (error) => {
      console.error('Error in deleteSession mutation:', error);
      toast.error('Failed to delete session');
    }
  });

  const retryProcessing = async (sessionId: string) => {
    const stemSession = sessions?.find(s => s.id === sessionId);
    const acapellaSession = acapellaSessions?.find(s => s.id === sessionId);
    const drumSession = drumSessions?.find(s => s.id === sessionId);
    const session = stemSession || acapellaSession || drumSession;
    const type = stemSession ? 'stem' : acapellaSession ? 'acapella' : 'drum';

    if (!session) {
      toast.error('Session not found');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      setProcessingError(null);

      const processingTime = await processingUtils.getProcessingDuration(session.original_audio_url);
      setProcessingDuration(processingTime);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 1;
        });
      }, processingTime * 10);

      await updateSession.mutateAsync({
        id: sessionId,
        stems: session.stems as Stem[] || [],
        status: 'processing'
      });

      const processedStems = await processingUtils.processStemSplit(
        session.original_audio_url, 
        type === 'acapella' ? 'acapella-extractor' : type === 'drum' ? 'drum-remover' : 'vocal-and-instrumental-stems'
      );
      const processing_minutes = processedStems.length;

      await updateSession.mutateAsync({
        id: sessionId,
        stems: processedStems,
        status: 'completed',
        processing_minutes
      });

      clearInterval(progressInterval);
      setProgress(100);
      toast.success('Your audio was processed successfully');
    } catch (error) {
      console.error('Error processing audio:', error);
      setProcessingError(error.message || 'Failed to process audio file');
      toast.error(error.message || 'Failed to process audio file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = async (file: File, type: SessionType) => {
    try {
      const existingSession = await sessionApi.checkExistingSession(file.name, type);
      if (existingSession) {
        toast.error('This file is already being processed');
        return;
      }

      setIsProcessing(true);
      setProgress(0);
      setProcessingError(null);
      
      const originalUrl = URL.createObjectURL(file);
      const processingTime = await processingUtils.getProcessingDuration(originalUrl);
      setProcessingDuration(processingTime);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 1;
        });
      }, processingTime * 10);

      const publicUrl = await processingUtils.uploadFile(file);
      console.log('File uploaded, creating session...');

      const newSession = await createSession.mutateAsync({
        original_filename: file.name,
        original_audio_url: publicUrl,
        status: 'processing',
        type
      });

      setActiveSession({ id: newSession.id, type });
      console.log('Session created, processing stems...');

      const processedStems = await processingUtils.processStemSplit(
        publicUrl,
        type === 'acapella' ? 'acapella-extractor' : type === 'drum' ? 'drum-remover' : 'vocal-and-instrumental-stems'
      );
      console.log('Stems processed, updating session...');
      
      const processing_minutes = processedStems.length;

      await updateSession.mutateAsync({
        id: newSession.id,
        stems: processedStems,
        status: 'completed',
        processing_minutes
      });

      clearInterval(progressInterval);
      setProgress(100);
      toast.success('Your audio was processed successfully');
    } catch (error) {
      console.error('Error processing audio:', error);
      setProcessingError(error.message || 'Failed to process audio file');
      toast.error(error.message || 'Failed to process audio file');
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    sessions,
    acapellaSessions,
    drumSessions,
    isLoadingSessions,
    createSession,
    updateSession,
    deleteSession,
    isProcessing,
    setIsProcessing,
    progress,
    setProgress,
    processingDuration,
    setProcessingDuration,
    processingError,
    setProcessingError,
    handleFileSelect,
    activeSession,
    setActiveSession,
    retryProcessing,
  };
};
